import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import { PrismIconButtonComponent, PrismIconComponent, PrismTooltipComponent } from '@shure/prism-angular-components';
import { ErrorLevel, MissingDevice } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceWithErrors } from '@shure/shared/angular/ui/components/error-devices-list';

import { ErrorIconInfo, RoomStatusData, RoomStatusDevice } from './models';

export const NOT_IN_A_ROOM_KEY = 'not_in_a_room';

interface RoomStatusRendererComponentContext {
	componentParent: {
		openMissingDevicesDialog: (
			roomId: string,
			missingDevices: MissingDevice[],
			devicesWithFirmwareErrors: DeviceWithErrors[]
		) => void;
		openRoom: (roomId: string) => void;
		onNewDevicesButtonClick: (event: Event, devices: RoomStatusDevice[] | undefined) => void;
	};
}
export type RoomStatusRendererComponentParams = ICellRendererParams<
	object,
	RoomStatusData,
	RoomStatusRendererComponentContext
>;

/**
 * ```sh-room-status-renderer``` is a view component for displaying room status component, such as warning and error icons
 */
@Component({
	selector: 'sh-room-status-renderer',
	templateUrl: './room-status-renderer.component.html',
	styleUrls: ['./room-status-renderer.component.scss'],
	imports: [
		CommonModule,
		AgGridModule,
		TranslocoModule,
		PrismIconComponent,
		PrismIconButtonComponent,
		PrismTooltipComponent
	],
	standalone: true
})
export class RoomStatusRendererComponent implements ICellRendererAngularComp {
	protected readonly roomStatusData = signal<RoomStatusData | null | undefined>(undefined);
	public params!: RoomStatusRendererComponentParams;

	// Used to indicate if the grid is grouped or aggregated (online devices vs online rooms, for example)
	public isRowGroup!: boolean;

	public readonly errorLevel = ErrorLevel;
	public message!: string;
	public shouldDisplayStatusData!: boolean;

	public agInit(params: RoomStatusRendererComponentParams): void {
		this.update(params);

		this.isRowGroup = params.node.field === 'statusCellData';
		this.shouldDisplayStatusData = !params.value?.shouldHideStatus && params.node.key !== NOT_IN_A_ROOM_KEY;
	}

	public refresh(params: RoomStatusRendererComponentParams): boolean {
		this.update(params);
		return true;
	}

	public openRoomIncompatibleOrMissingDevicesDialog(event: Event): void {
		event.stopPropagation();

		const data = this.roomStatusData();
		if (data?.errorType === 'RoomIncompatible') {
			this.params.context.componentParent.openRoom(data.id);
		} else if (!!data && data.id && data.missingDevices.length + data.devicesWithFirmwareErrors.length > 0) {
			this.params.context.componentParent.openMissingDevicesDialog(
				data.id,
				data.missingDevices,
				data.devicesWithFirmwareErrors
			);
		}
	}

	public onNewDevicesButtonClick(event: Event): void {
		event.stopPropagation();

		if (this.roomStatusData()) {
			this.params.context.componentParent.onNewDevicesButtonClick(event, this.roomStatusData()?.devices);
		}
	}

	private update(params: RoomStatusRendererComponentParams): void {
		this.params = params;
		if (params.value) {
			this.roomStatusData.set({ ...params.value, errorIcon: this.getErrorIconInfo(params.value.errorLevel) });
		}
	}

	private getErrorIconInfo(errorLevel?: ErrorLevel): ErrorIconInfo {
		switch (errorLevel) {
			case ErrorLevel.Warning:
				return { name: 'sh-warning-outline', colorToken: 'var(--prism-color-theme-state-ok)' };
			case ErrorLevel.Error:
				return { name: 'sh-error-outline', colorToken: 'var(--prism-color-theme-state-bad)' };
			case ErrorLevel.RFScanning:
				return { name: 'radar', colorToken: 'var(--prism-color-theme-brand-orange)' };
			case ErrorLevel.EncryptionError:
				return { name: 'vpn_key', colorToken: 'var(--prism-color-theme-state-bad)' };
			default:
				return { name: '', colorToken: '' };
		}
	}
}
